import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Eye } from "lucide-react";
import { useImportCommunityTopic } from "@/hooks/useImportCommunityTopic";
import { Skeleton } from "@/components/ui/skeleton";
import { useEffect, useRef } from "react";
import LessonItem from "@/components/chat/LessonItem";
import { Lesson } from "@/types/course-outline";

const formatCount = (count: number): string => {
  if (count >= 1000000) return `${(count / 1000000).toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000).toFixed(1)}K`;
  return count.toString();
};

const CommunityTopicDetailPage = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const importTopic = useImportCommunityTopic();
  const hasIncrementedView = useRef(false);

  // Transform community topic lessons to match Lesson interface
  const transformCommunityLessons = (lessons: any[]): Lesson[] => {
    return lessons.map((lesson, index) => {
      // Extract title from lesson_context_prompt (first line after "Lesson:")
      const titleMatch = lesson.lesson_context_prompt?.match(/Lesson:\s*(.+?)(?:\n|$)/);
      const title = titleMatch?.[1]?.trim() || lesson.title || `Lesson ${index + 1}`;
      return {
        id: `lesson-${index}`,
        title,
        lessonNumber: index + 1,
        duration: `${lesson.duration_minutes || 20} min`,
        status: 'generated' as const,
        completed: false,
        completionPercentage: 0
      };
    });
  };
  const {
    data: topic,
    isLoading
  } = useQuery({
    queryKey: ['community-topic', id],
    queryFn: async () => {
      const {
        data,
        error
      } = await supabase.from('community_topics').select('*').eq('id', id).eq('published', true).single();
      if (error) throw error;
      return data;
    },
    enabled: !!id
  });

  // Increment view count on mount (only once)
  useEffect(() => {
    if (id && topic && !hasIncrementedView.current) {
      hasIncrementedView.current = true;
      const newCount = (topic.view_count || 0) + 1;

      // Optimistically update the local cache first
      queryClient.setQueryData(['community-topic', id], {
        ...topic,
        view_count: newCount
      });

      // Then update in the database via secure edge function
      supabase.functions
        .invoke('community-topic-metrics', {
          body: { topicId: id, action: 'view' },
        })
        .catch((err) => console.warn('Failed to increment view count:', err));
    }
  }, [id, topic, queryClient]);
  const handleImport = () => {
    if (id) {
      importTopic.mutate(id);
    }
  };
  if (isLoading) {
    return <AppLayout pageTitle="Loading...">
        <div className="h-full bg-app-background p-6 md:p-8 overflow-y-auto">
          <div className="max-w-4xl mx-auto space-y-6">
            <Skeleton className="h-10 w-32" />
            <Skeleton className="h-12 w-3/4" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        </div>
      </AppLayout>;
  }
  if (!topic) {
    return <AppLayout pageTitle="Topic Not Found">
        <div className="h-full bg-app-background flex items-center justify-center p-6">
          <div className="text-center">
            <h1 className="text-2xl font-medium text-slate-900 mb-2">Topic Not Found</h1>
            <p className="text-slate-500 mb-6">This topic doesn't exist or is no longer available.</p>
            <Button onClick={() => navigate('/explore')}>
              Back to Explore
            </Button>
          </div>
        </div>
      </AppLayout>;
  }
  return <AppLayout pageTitle={`${topic.title} - StudiBudi`}>
      <div className="h-full bg-app-background overflow-y-auto">
        <div className="max-w-4xl mx-auto p-6 md:p-8 space-y-6">
          {/* Back Button */}
          <Button variant="ghost" onClick={() => navigate('/explore')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Explore
          </Button>

          {/* Header */}
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-4">
              <h1 className="text-3xl md:text-4xl font-medium text-slate-900">
                {topic.title}
              </h1>
              <div className="flex flex-col items-end gap-1">
                <Button onClick={handleImport} disabled={importTopic.isPending} className="flex items-center gap-2">
                  <Download className="w-4 h-4" />
                  {importTopic.isPending ? 'Adding...' : 'Add to Workspace'}
                </Button>
                {(topic.import_count || 0) > 0 && (
                  <span className="text-xs text-slate-500">
                    Added by {formatCount(topic.import_count || 0)} {(topic.import_count || 0) === 1 ? 'user' : 'users'}
                  </span>
                )}
              </div>
            </div>

            {/* Stats */}
            <div className="flex gap-4 text-sm text-slate-500">
              <span className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                {topic.view_count || 0} views
              </span>
              <span className="flex items-center gap-1">
                <Download className="w-4 h-4" />
                {formatCount(topic.import_count || 0)} added
              </span>
            </div>

          </div>

          {/* Thumbnail */}
          {topic.thumbnail_url && <div className="rounded-xl overflow-hidden border border-gray-200">
              <img src={topic.thumbnail_url} alt={topic.title} className="w-full h-64 object-cover" />
            </div>}

          {/* Course Outline */}
          {topic.course_outline && typeof topic.course_outline === 'object' && <div className="bg-white rounded-xl border border-gray-200 p-6">
              
              <div className="space-y-4">
                {(topic.course_outline as any).course_description && <p className="text-slate-600 leading-relaxed text-sm font-extralight">
                    {(topic.course_outline as any).course_description}
                  </p>}
                {(topic.course_outline as any).target_audience && <div>
                    <h3 className="font-medium text-slate-900 mb-1">Target Audience</h3>
                    <p className="text-slate-600">{(topic.course_outline as any).target_audience}</p>
                  </div>}
                {(topic.course_outline as any).prerequisites && <div>
                    <h3 className="font-medium text-slate-900 mb-1">Prerequisites</h3>
                    <p className="text-slate-600">{(topic.course_outline as any).prerequisites}</p>
                  </div>}
                
                {/* Tags */}
                {topic.tags && topic.tags.length > 0 && <div className="flex gap-2 flex-wrap">
                    {topic.tags.map((tag: string, idx: number) => <span key={idx} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                        {tag}
                      </span>)}
                  </div>}
              </div>
            </div>}

          {/* Course Content */}
          {topic.lessons && Array.isArray(topic.lessons) && topic.lessons.length > 0 && <div className="space-y-4">
              <h2 className="font-medium text-slate-900 text-lg">Included Lessons</h2>
              <div className="space-y-2">
                {transformCommunityLessons(topic.lessons).map(lesson => <LessonItem key={lesson.id} lesson={lesson} onClick={() => {}} />)}
              </div>
            </div>}

          {/* Import CTA */}
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-xl border border-gray-200 p-8 text-center">
            <h3 className="text-xl font-medium text-slate-900 mb-2">
              Ready to start learning?
            </h3>
            <p className="text-slate-600 mb-4">
              Add this topic to your workspace and begin your learning journey.
            </p>
            <Button onClick={handleImport} disabled={importTopic.isPending} size="lg" className="min-w-[200px]">
              <Download className="w-4 h-4 mr-2" />
              {importTopic.isPending ? 'Adding...' : 'Add to My Workspace'}
            </Button>
          </div>
        </div>
      </div>
    </AppLayout>;
};
export default CommunityTopicDetailPage;